import { PickIndex } from "./chartGame.ts";
import { Direction, makesTiersFor, MULTIPLICATION_ONLY, playsMultiples, poolsFor, Rule, RuleKind, Square } from "./munchGame.ts";

/*
 * Collie Crossing (ADR 0057), as pure functions: a floor of numbered tiles,
 * a rule, and a border collie crossing from the start ledge to the far side.
 * A tile holds exactly when it fits the rule. Randomness comes in through
 * `pick`, so tests can fix it.
 */

export const COLS = 5;
export const ROWS = 7;
/** The ledge below the floor, and the far side above it. */
export const START_ROW = -1;
export const GOAL_ROW = ROWS;
export const START_COL = 2;

export interface Tile extends Omit<Square, "eaten"> {
    broken: boolean;
    /** Stepped on safely at least once - it shows a paw print. */
    stepped: boolean;
}

export interface Position {
    row: number;
    col: number;
}

export type CollieEvent =
    | { kind: "stepped"; seq: number; tile: Tile }
    | { kind: "fell"; seq: number; tile: Tile }
    | { kind: "crossed"; seq: number }
    | { kind: "floor"; seq: number };

export interface CollieState {
    floor: number;
    rule: Rule;
    /** Row 0 is next to the start ledge; index = row * COLS + col. */
    tiles: Tile[];
    dog: Position;
    /** The operations the player chose; "Makes n" tiles come from these (ADR 0098 §4). */
    operations: string[];
    phase: "walking" | "falling" | "crossed";
    /** Falls on this floor. */
    falls: number;
    /** Distinct tiles stepped on safely on this floor. */
    safeSteps: number;
    totalFalls: number;
    crossings: number;
    lastEvent: CollieEvent | null;
}

const CYCLE: RuleKind[] = ["multiples", "multiples", "makes", "multiples", "factors"];
const MULTIPLES = [3, 4, 6, 7, 8, 9, 11, 12];
const PRODUCTS = [[12, 24], [36, 48], [60, 72]];
const FACTORS = [[24, 36], [48, 60], [72]];
/** With nothing but "Makes n" to play, the target tier rises this often. */
const MAKES_EVERY = 3;

/**
 * Floor 1 is multiples of 3, as asked; see ADR 0057 §3 for the order after.
 * Without x or / there are no multiples or factors to play, so every floor
 * makes a target (ADR 0098 §4).
 */
export function ruleForFloor(floor: number, pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): Rule {
    const index = floor - 1;
    if (!playsMultiples(operations)) {
        const tiers = makesTiersFor(operations, PRODUCTS);
        const tier = tiers[Math.min(Math.floor(index / MAKES_EVERY), tiers.length - 1)];
        return { kind: "makes", n: tier[pick(tier.length)] };
    }
    const cycle = Math.floor(index / CYCLE.length);
    const step = index % CYCLE.length;
    const kind = CYCLE[step];
    if (kind === "multiples") {
        const nth = cycle * 3 + (step === 0 ? 0 : step === 1 ? 1 : 2);
        const n = nth < MULTIPLES.length ? MULTIPLES[nth] : MULTIPLES.slice(-3)[pick(3)];
        return { kind, n };
    }
    const tiers = kind === "makes" ? makesTiersFor(operations, PRODUCTS) : FACTORS;
    const tier = tiers[Math.min(cycle, tiers.length - 1)];
    return { kind, n: tier[pick(tier.length)] };
}

export function indexOf(pos: Position): number {
    return pos.row * COLS + pos.col;
}

/**
 * The route laid first (§1): one tile per row from the bottom up, sometimes
 * with a step sideways before going up again, so it is always connected by
 * the moves the dog can make.
 */
export function safePath(pick: PickIndex): Set<number> {
    const path = new Set<number>();
    let col = pick(COLS);
    for (let row = 0; row < ROWS; row++) {
        path.add(indexOf({ row, col }));
        if (row < ROWS - 1 && pick(2) === 0) {
            let next = col + (pick(2) === 0 ? -1 : 1);
            if (next < 0 || next >= COLS) {
                next = col + (col === 0 ? 1 : -1);
            }
            col = next;
            path.add(indexOf({ row, col }));
        }
    }
    return path;
}

/**
 * A floor that can always be crossed (§1). The safe path makes it so by
 * construction; the check makes it so by inspection too, and deals again if a
 * future change to the generation ever breaks the construction - a floor the
 * dog cannot cross is never handed out.
 */
export function floorFor(rule: Rule, pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): Tile[] {
    const { fit, miss } = poolsFor(rule, operations);
    for (;;) {
        const path = safePath(pick);
        const tiles = Array.from({ length: ROWS * COLS }, (_, i) => {
            const fits = path.has(i) || pick(3) === 0;
            const pool = fits ? fit : miss;
            const square = pool[pick(pool.length)];
            return { value: square.value, op: square.op, a: square.a, b: square.b, fits, broken: false, stepped: false };
        });
        if (hasSafeRoute(tiles)) {
            return tiles;
        }
    }
}

function newFloor(
    floor: number,
    pick: PickIndex,
    operations: string[]
): Pick<CollieState, "floor" | "rule" | "tiles" | "dog" | "phase" | "falls" | "safeSteps"> {
    const rule = ruleForFloor(floor, pick, operations);
    return {
        floor,
        rule,
        tiles: floorFor(rule, pick, operations),
        dog: { row: START_ROW, col: START_COL },
        phase: "walking",
        falls: 0,
        safeSteps: 0,
    };
}

export function startCollie(pick: PickIndex, operations: string[] = MULTIPLICATION_ONLY): CollieState {
    return { ...newFloor(1, pick, operations), operations, totalFalls: 0, crossings: 0, lastEvent: null };
}

function nextSeq(state: CollieState): number {
    return (state.lastEvent?.seq ?? 0) + 1;
}

export function neighbour(pos: Position, dir: Direction): Position {
    if (dir === "up") return { row: pos.row + 1, col: pos.col };
    if (dir === "down") return { row: pos.row - 1, col: pos.col };
    if (dir === "left") return { row: pos.row, col: pos.col - 1 };
    return { row: pos.row, col: pos.col + 1 };
}

/** The direction from one position to a neighbouring one, or null. */
export function directionBetween(from: Position, to: Position): Direction | null {
    const dirs: Direction[] = ["up", "down", "left", "right"];
    return dirs.find((d) => {
        const n = neighbour(from, d);
        return n.row === to.row && n.col === to.col;
    }) ?? null;
}

/**
 * One step. Onto a tile that fits: it holds and gets a paw print. Onto one
 * that does not: it breaks and she falls. A broken tile is a hole she will not
 * walk into again (§2). The ledges are always safe.
 */
export function move(state: CollieState, dir: Direction): CollieState {
    if (state.phase !== "walking") {
        return state;
    }
    const to = neighbour(state.dog, dir);
    if (to.col < 0 || to.col >= COLS || to.row < START_ROW) {
        return state;
    }
    if (to.row === GOAL_ROW) {
        return {
            ...state,
            dog: to,
            phase: "crossed",
            crossings: state.crossings + 1,
            lastEvent: { kind: "crossed", seq: nextSeq(state) },
        };
    }
    if (to.row === START_ROW) {
        return { ...state, dog: to };
    }
    const index = indexOf(to);
    const tile = state.tiles[index];
    if (tile.broken) {
        return state;
    }
    if (tile.fits) {
        const tiles = state.tiles.map((t, i) => (i === index ? { ...t, stepped: true } : t));
        return {
            ...state,
            dog: to,
            tiles,
            safeSteps: state.safeSteps + (tile.stepped ? 0 : 1),
            lastEvent: { kind: "stepped", seq: nextSeq(state), tile },
        };
    }
    const tiles = state.tiles.map((t, i) => (i === index ? { ...t, broken: true } : t));
    return {
        ...state,
        dog: to,
        tiles,
        phase: "falling",
        falls: state.falls + 1,
        totalFalls: state.totalFalls + 1,
        lastEvent: { kind: "fell", seq: nextSeq(state), tile },
    };
}

/** After the fall: back on the start ledge, with the floor as it now is. */
export function respawn(state: CollieState): CollieState {
    if (state.phase !== "falling") {
        return state;
    }
    return { ...state, dog: { row: START_ROW, col: START_COL }, phase: "walking" };
}

export function nextFloor(state: CollieState, pick: PickIndex): CollieState {
    if (state.phase !== "crossed") {
        return state;
    }
    return { ...state, ...newFloor(state.floor + 1, pick, state.operations), lastEvent: { kind: "floor", seq: nextSeq(state) } };
}

/** Three with no falls, two with one or two, one otherwise (§2). */
export function starsFor(falls: number): number {
    return falls === 0 ? 3 : falls <= 2 ? 2 : 1;
}

/** Whether the tiles that hold join the bottom row to the top - true of every floor dealt. */
export function hasSafeRoute(tiles: Tile[]): boolean {
    const seen = new Set<number>();
    const queue: Position[] = [];
    for (let col = 0; col < COLS; col++) {
        if (tiles[indexOf({ row: 0, col })].fits) {
            queue.push({ row: 0, col });
            seen.add(indexOf({ row: 0, col }));
        }
    }
    while (queue.length > 0) {
        const pos = queue.shift()!;
        if (pos.row === ROWS - 1) {
            return true;
        }
        for (const dir of ["up", "down", "left", "right"] as Direction[]) {
            const n = neighbour(pos, dir);
            if (n.row < 0 || n.row >= ROWS || n.col < 0 || n.col >= COLS) continue;
            const i = indexOf(n);
            if (!seen.has(i) && tiles[i].fits) {
                seen.add(i);
                queue.push(n);
            }
        }
    }
    return false;
}
